import is from '@sindresorhus/is';
import type { Location } from 'react-router';
import { matchPath } from 'react-router';

import type { FeatureFlagService } from '~/shared/feature-flag-service/feature-flag-service';

import type { UrlTileDefinition } from '../types';
import type { TileAvailability, TileRoutePolicy } from './types';

export type ResolveTileAvailabilityDependencies = {
    readonly definitions: readonly UrlTileDefinition[];
    readonly featureFlagService: FeatureFlagService;
};

export type ResolvedTileAvailability = {
    readonly availabilityByType: ReadonlyMap<string, TileAvailability>;
    readonly declaredTypes: ReadonlySet<string>;
    readonly definitions: readonly UrlTileDefinition[];
};

const countPatternSegments = (pattern: string): number => pattern.split('/').filter(is.nonEmptyString).length;

const comparePatternSpecificity = (first: TileRoutePolicy, second: TileRoutePolicy): number => {
    const segmentDifference = countPatternSegments(second.pattern) - countPatternSegments(first.pattern);

    return segmentDifference === 0 ? second.pattern.length - first.pattern.length : segmentDifference;
};

const findMatchingTileRoutePolicy = (
    pathname: string,
    policies: readonly TileRoutePolicy[],
): TileRoutePolicy | undefined => {
    const matchingPolicies = policies.filter((policy) => !is.null(matchPath(policy.pattern, pathname)));

    return [...matchingPolicies].sort(comparePatternSpecificity)[0];
};

export const resolveTileAvailability = (
    location: Pick<Location, 'pathname'>,
    policies: readonly TileRoutePolicy[],
    dependencies: ResolveTileAvailabilityDependencies,
): ResolvedTileAvailability => {
    const declaredTiles = findMatchingTileRoutePolicy(location.pathname, policies)?.tiles ?? [];
    const enabledTiles = declaredTiles.filter(
        (tile) =>
            is.undefined(tile.behindFeatureName) ||
            dependencies.featureFlagService.isFeatureFlagEnabled(tile.behindFeatureName),
    );
    const availabilityByType = new Map<string, TileAvailability>(enabledTiles.map((tile) => [tile.type, tile]));

    return {
        availabilityByType,
        declaredTypes: new Set(declaredTiles.map((tile) => tile.type)),
        definitions: dependencies.definitions.filter((definition) => availabilityByType.has(definition.type)),
    };
};
